/**
 * The /upay/ hub check's form props, frame, slot floors and ids, shared by the
 * server-rendered shell (UpayChartCheckLazy), the island's BirthDetailsForm
 * and BirthToolFormShell.parity.test.tsx, so every frame before and after the
 * island loads reads the same words. No name field: the answer is the reader's
 * own chart. The birth time is optional: the date alone settles the sign, and
 * so the dignity, of the grahas that hold one sign all day; combust and the
 * house need the time.
 *
 * The /hi/upay/ twin (UPAY_CHART_CHECK_HI_FORM and UPAY_CHART_CHECK_HI_COPY
 * below) is Devanagari (owner decision 2026-10-28, which replaced the
 * 2026-10-08 Hinglish rule), the register of BirthDetailsForm.i18n.ts:
 * tests/hindi-register.test.ts keeps Latin-letter Hinglish out of the Hindi
 * copy. The Hindi section's heading and lead and the Hindi answer's text are
 * page copy and are Devanagari in their components (UpayChartCheckSection.tsx,
 * UpayChartAnswer.tsx).
 */
export const UPAY_CHART_CHECK_FORM = {
  language: "en",
  fieldIdScope: "upay-check",
  submitLabel: "Check My Grahas",
  collectName: false,
  requireBirthTime: false,
} as const;

/**
 * One box for every state of the check's slot (the /graha/ check's frame): the
 * shell, the loading shell, the live form and the answer each fill the slot's
 * reserved height, and the submit sits at the foot of the box in every state
 * with one 48 px height.
 */
export const UPAY_CHART_CHECK_FRAME =
  "mx-auto flex w-full max-w-md flex-1 flex-col [&>form]:flex [&>form]:flex-1 [&>form]:flex-col [&>form>:last-child]:mt-auto [&>form>:last-child]:min-h-12";

/** The answer is a nine-row table, so it takes the section's full width, not the form's. */
export const UPAY_CHART_ANSWER_FRAME = "flex w-full flex-1 flex-col";

/**
 * The form's room: the slot's floor and, once the answer's room is added, the
 * form frame's own height, so the submit stays where the press found it.
 * UpayChartCheckLazy has the measured heights.
 */
export const UPAY_CHART_FORM_RESERVE = "min-h-[600px] sm:min-h-[540px]";

/**
 * The answer's room, added by the island inside the submit itself, before the
 * request goes out (the /graha/ and career checks' pattern): the growth
 * happens in the press, an input-driven change rather than a layout shift,
 * and the reply, an error or a retry all land inside it; only "Check another
 * birth date" or "Add your birth time" releases it. UpayChartCheckLazy has
 * the measured heights of the tallest answer.
 */
export const UPAY_CHART_ANSWER_RESERVE = "min-h-[2400px] sm:min-h-[1660px]";

/**
 * The real form's input ids, scoped by fieldIdScope: /hi/upay/ also holds the
 * remedies tool's form, whose "-default" ids a label or a focus hand-off would
 * otherwise reach (BirthDetailsForm's fieldId).
 */
export const UPAY_CHART_CHECK_FIELD_IDS = {
  date: "birthDate-upay-check",
  time: "birthTime-upay-check",
  place: "birthPlace-upay-check",
} as const;

/** free_tool_started and calculator_used placement for the /upay/ hub check. */
export const UPAY_CHART_CHECK_PLACEMENT = "upay_hub_check";

/** free_tool_started and calculator_used placement for the /hi/upay/ hub check. */
export const UPAY_CHART_CHECK_HI_PLACEMENT = "hi_upay_hub_check";

/**
 * The Hindi check's controls: the submit, the failure lines and the answer's
 * two buttons. "ग्रह जाँचें" stays on one line at 320 px (the
 * 2026-10-14 graha "jaanein" lesson: a label that wraps grows back when the
 * one-line loading label replaces it).
 */
export const UPAY_CHART_CHECK_HI_COPY = {
  submit: "ग्रह जाँचें",
  unavailable: "गणना नहीं हो पाई — कृपया दोबारा कोशिश करें।",
  unreadable: "यह परिणाम पढ़ा नहीं जा सका। कृपया दोबारा कोशिश करें।",
  checkAnother: "दूसरी जन्म तिथि जाँचें",
  addTime: "जन्म समय डालें",
} as const;

/** The Hindi form props: no name field and an optional birth time, as the English form. */
export const UPAY_CHART_CHECK_HI_FORM = {
  language: "hi",
  fieldIdScope: "upay-check",
  submitLabel: UPAY_CHART_CHECK_HI_COPY.submit,
  collectName: false,
  requireBirthTime: false,
} as const;
